import {
  AlertTriangle,
  ArrowRight,
  Check,
  CheckCircle2,
  Copy,
  Download,
  Eye,
  EyeOff,
  Info,
  KeyRound,
  Lock,
  Mail,
  RotateCw,
  ShieldCheck,
  Smartphone,
  XCircle,
} from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { z } from "zod";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { update2FAStatus } from "@/modules/auth/store/authSlice";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaOtpInput } from "@/shared/components/forms/ColocaOtpInput";
import { ColocaPasswordField } from "@/shared/components/forms/ColocaPasswordField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { AuthenticatorIllustration } from "@/shared/components/security/AuthenticatorIllustration";
import { MethodIllustrationStage } from "@/shared/components/security/MethodIllustrationStage";
import {
  SecurityPinDialog,
  type SecurityPinDialogMode,
} from "@/shared/components/security/SecurityPinDialog";
import { SecurityPinIllustration } from "@/shared/components/security/SecurityPinIllustration";
import {
  SecurityPinSetupActions,
  SecurityPinSetupBody,
  SecurityPinStepper,
} from "@/shared/components/security/SecurityPinSetup";
import { useSecurityPinSetup } from "@/shared/components/security/useSecurityPinSetup";
import { useAddressQr } from "@/shared/hooks/useAddressQr";
import { useCopyToClipboard } from "@/shared/hooks/useCopyToClipboard";
import { useNow } from "@/shared/hooks/useNow";
import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { readApiCode } from "@/shared/utils/apiError";
import {
  canUseSecurityPin,
  formatLockRemaining,
  stepUpLockOf,
  stepUpMethodOf,
  supportsEmailBackup,
} from "@/shared/utils/stepUp";
import { colocaToast } from "@/shared/utils/toast";

import { settingsService } from "../services/settingsService";
import { Disable2FAModal } from "./Disable2FAModal";
import { EmailBackupCard } from "./EmailBackupCard";
import { RemoveSecurityPinDialog } from "./RemoveSecurityPinDialog";
import { SettingsSection } from "./SettingsSection";
import { StepUpMethodGuide } from "./StepUpMethodGuide";

type Method = "totp" | "email" | "pin";

// Drawn in the browser: the otpauth:// URL carries the TOTP secret, so it must never reach a third party.
function QrCode({ value, size = 140 }: { value: string; size?: number }) {
  const qr = useAddressQr(value);
  if (!value || !qr.src) {
    return (
      <div
        className="grid place-items-center rounded-md border border-border bg-muted/30"
        style={{ width: size, height: size }}
      >
        <ColocaSkeleton className="h-full w-full" />
      </div>
    );
  }
  return (
    <div
      className="grid place-items-center rounded-md border border-border bg-white p-2"
      style={{ width: size, height: size }}
    >
      <img src={qr.src} alt="2FA QR" width={size - 16} height={size - 16} />
    </div>
  );
}

export function SecuritySection() {
  const { t } = useTranslations("settings");
  const { t: tg } = useTranslations("global");
  const { user } = useAuth();
  const dispatch = useDispatch();

  const enabled = !!user?.two_factor_enabled;
  // The security PIN is the faster, less secure alternative for organization members; staff stay on the app.
  const pinAllowed = canUseSecurityPin(user);
  const pinActive = !enabled && stepUpMethodOf(user) === "pin";
  // The plan B of whichever method is chosen: on by default with the PIN, opt-in with the app.
  const emailBackup = supportsEmailBackup(user);
  // Five wrong codes in a row pause step-up for either method; only the PIN also has the hard lock.
  const lockedUntil = user?.step_up_locked_until ?? null;
  const now = useNow((pinActive || enabled) && !!lockedUntil);
  const lock = stepUpLockOf(user, now);
  const pinLock = pinActive ? lock : null;
  const totpLock = enabled && lock?.kind === "temporary" ? lock : null;

  // With a PIN already working, nothing is picked: the app's setup opens only when the person asks for it.
  const [method, setMethod] = useState<Method | null>(() => (pinActive ? null : "totp"));
  const [pinDialog, setPinDialog] = useState<SecurityPinDialogMode | null>(null);
  const [removePinOpen, setRemovePinOpen] = useState(false);
  /** Keeps the inline PIN flow on its success state after the section has already switched to "PIN active". */
  const [pinJustCreated, setPinJustCreated] = useState(false);

  /** Enrolment asks for the account password before the server mints a secret: a session alone is not enough. */
  const [totpStage, setTotpStage] = useState<"password" | "scan">("password");
  const [secret, setSecret] = useState<string>("");
  const [qrUrl, setQrUrl] = useState<string>("");
  const [showSecret, setShowSecret] = useState(false);
  const [otp, setOtp] = useState<string>("");
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [isLoadingSetup, setIsLoadingSetup] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [isRegenerating, setIsRegenerating] = useState(false);

  const { copiedValue, copy } = useCopyToClipboard(t("copy_error_message"));

  // One derived list, so the actions, the warning and the grid cannot disagree about whether there are
  // codes on screen — they were asking two different questions a few lines apart.
  const codes = recoveryCodes ?? [];
  const hasCodes = codes.length > 0;
  // One code per line, no index prefix: the `01`/`02` labels are drawn by the grid and are not part of the
  // code. This is byte-for-byte what the .txt download writes, so the clipboard and the file never diverge.
  const codesText = codes.join("\n");
  const hasCopiedCodes = hasCodes && copiedValue === codesText;

  // The password answers the server's re-authentication; only then does it mint the secret and the QR.
  const startTotpSetup = async (password: string): Promise<string | null> => {
    setIsLoadingSetup(true);
    try {
      const data = await settingsService.enable2FA(password);
      setSecret(data.secret);
      setQrUrl(data.qrCode);
      setOtp("");
      setVerifyError(null);
      setTotpStage("scan");
      return null;
    } catch (error: unknown) {
      const code = readApiCode(error);
      // A wrong current password counts toward the login lock, so the sentence says so before the fifth one.
      if (code === "PASSWORD_INCORRECT") return tg("reauth_password_incorrect_hint");
      if (code === "PASSWORD_REQUIRED") return t("security_totp_password_required");
      return HttpApiService.getErrorMessage(error) || t("error_generic");
    } finally {
      setIsLoadingSetup(false);
    }
  };

  const handleVerify = async (value = otp) => {
    if (value.length !== 6 || !secret || isVerifying) return;
    setIsVerifying(true);
    setVerifyError(null);
    try {
      const result = await settingsService.verify2FA(value);
      dispatch(update2FAStatus({ enabled: true }));
      if (result.recovery_codes?.length) {
        setRecoveryCodes(result.recovery_codes);
      }
      colocaToast.success(t("enable_twofa_success"));
      setOtp("");
    } catch (error) {
      const message = HttpApiService.getErrorMessage(error);
      colocaToast.error(message || t("error_invalid_code"));
      setVerifyError(message || t("error_invalid_code"));
      setOtp("");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleOtpChange = (next: string) => {
    if (verifyError) setVerifyError(null);
    setOtp(next);
  };

  const handleRegenerateCodes = async () => {
    setIsRegenerating(true);
    try {
      const result = await settingsService.regenerateRecoveryCodes();
      setRecoveryCodes(result.recovery_codes);
      colocaToast.success(t("security_recovery_regenerated"));
    } catch (error) {
      colocaToast.error(HttpApiService.getErrorMessage(error));
    } finally {
      setIsRegenerating(false);
    }
  };

  // The secret's control is icon-only, so a toast is its only possible feedback. The recovery codes' button
  // carries a label and swaps it instead, which is why only this one asks for a toast.
  const handleCopySecret = () => {
    if (!secret) return;
    void copy(secret, t("copied_message"));
  };

  const handleCopyCodes = () => {
    if (!codesText) return;
    void copy(codesText);
  };

  const handleDownloadCodes = () => {
    if (!codesText) return;
    const blob = new Blob([codesText], { type: "text/plain" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "coloca-recovery-codes.txt";
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const handleDisableSuccess = () => {
    dispatch(update2FAStatus({ enabled: false }));
    setDisableOpen(false);
    setRecoveryCodes(null);
    setOtp("");
    // A new enrolment starts from the password again: the old secret was dropped with the method.
    setTotpStage("password");
    setSecret("");
    setQrUrl("");
  };

  return (
    <>
      <SettingsSection
        id="seguridad"
        eyebrow={t("security_section_title")}
        title={t("security_2fa_title")}
        sub={t("security_2fa_sub")}
        headerRight={
          enabled ? (
            <ColocaBadge variant="success" icon={CheckCircle2}>
              {t("twofa_status_enabled")}
            </ColocaBadge>
          ) : pinActive ? (
            <ColocaBadge variant="primary" icon={KeyRound}>
              {t("security_pin_status_badge")}
            </ColocaBadge>
          ) : (
            <ColocaBadge variant="warning" icon={AlertTriangle}>
              {t("security_2fa_status_not_configured")}
            </ColocaBadge>
          )
        }
      >
        {/* Status banner */}
        {pinActive ? (
          <PinStatusBanner
            lockText={
              pinLock?.kind === "temporary"
                ? t("security_pin_locked_temporary", {
                    time: formatLockRemaining(pinLock.until.getTime() - now),
                  })
                : pinLock?.kind === "reset"
                  ? t("security_pin_locked_reset")
                  : null
            }
            onChange={() => setPinDialog(pinLock ? "reset" : "change")}
            onRemove={() => setRemovePinOpen(true)}
          />
        ) : (
          <div
            className={cn(
              "mb-[18px] flex flex-wrap items-center gap-3.5 rounded-md border p-3.5",
              enabled ? "border-primary/30 bg-primary/5" : "border-warning/40 bg-warning/10",
            )}
          >
            <div
              className={cn(
                "grid h-9 w-9 shrink-0 place-items-center rounded-lg border bg-background",
                enabled ? "border-primary/30 text-primary" : "border-warning/40 text-warning",
              )}
            >
              {enabled ? <ShieldCheck size={16} /> : <Lock size={16} />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-semibold text-foreground">
                {enabled ? t("security_2fa_protected_title") : t("security_2fa_unprotected_title")}
              </div>
              <div className="mt-0.5 text-[12px] text-muted-foreground">
                {enabled ? t("security_2fa_protected_sub") : t("security_2fa_unprotected_sub")}
              </div>
            </div>
            {enabled && (
              <ColocaButton
                variant="ghost"
                size="sm"
                icon={XCircle}
                onClick={() => setDisableOpen(true)}
                className="w-full text-destructive sm:w-auto"
              >
                {t("twofa_disable_button")}
              </ColocaButton>
            )}
            {enabled && pinAllowed && (
              <div className="flex w-full items-center gap-1.5 border-t border-primary/15 pt-2.5 text-[12px] text-muted-foreground">
                <Info size={13} className="shrink-0 text-primary" aria-hidden="true" />
                {t("security_totp_replaces_pin")}
              </div>
            )}
            {totpLock ? (
              <ColocaAlert
                variant="warning"
                icon={Lock}
                heading={t("security_totp_locked_title")}
                text={t("security_totp_locked", {
                  time: formatLockRemaining(totpLock.until.getTime() - now),
                })}
                className="w-full animate-fade-down animate-duration-300 motion-reduce:animate-none"
              />
            ) : null}
          </div>
        )}

        {/* Setup flow — visible by default when 2FA is disabled */}
        {!enabled && (
          <>
            <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
              {pinActive ? t("security_upgrade_pick") : t("security_2fa_pick_method")}
            </div>
            <div className="mb-3 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              <MethodCard
                id="totp"
                icon={Smartphone}
                illustration={<AuthenticatorIllustration />}
                name={t("security_method_totp_name")}
                sub={pinActive ? t("security_method_totp_sub_upgrade") : t("security_method_totp_sub")}
                tag={
                  pinAllowed
                    ? t("security_method_tag_recommended_secure")
                    : t("security_method_tag_recommended")
                }
                tagVariant="success"
                note={emailBackup ? t("security_method_totp_email_note") : undefined}
                active={method === "totp"}
                onClick={() => setMethod("totp")}
                className="animate-fade-up animate-duration-300 motion-reduce:animate-none"
              />
              {pinAllowed ? (
                <MethodCard
                  id="pin"
                  icon={KeyRound}
                  illustration={<SecurityPinIllustration />}
                  name={t("security_method_pin_name")}
                  sub={pinActive ? t("security_method_pin_sub_active") : t("security_method_pin_sub")}
                  tag={pinActive ? t("security_method_tag_active") : t("security_method_tag_fastest")}
                  tagVariant="primary"
                  note={emailBackup ? t("security_method_pin_email_note") : undefined}
                  active={method === "pin"}
                  onClick={() => setMethod(pinActive ? null : "pin")}
                  className="animate-fade-up animate-delay-75 animate-duration-300 motion-reduce:animate-none"
                />
              ) : (
                <MethodCard
                  id="email"
                  icon={Mail}
                  name={t("security_method_email_name")}
                  sub={t("security_method_email_sub")}
                  tag={t("security_method_tag_coming_soon")}
                  tagVariant="secondary"
                  disabled
                  active={method === "email"}
                  onClick={() => {}}
                />
              )}
            </div>
            {emailBackup ? (
              <StepUpMethodGuide className="mb-5" />
            ) : pinAllowed ? (
              <p className="mb-5 flex items-start gap-1.5 text-[12px] text-muted-foreground">
                <ShieldCheck size={13} className="mt-0.5 shrink-0 text-success" aria-hidden="true" />
                {t("security_methods_compare_note")}
              </p>
            ) : (
              <div className="mb-2" />
            )}

            {method === "pin" && (!pinActive || pinJustCreated) && (
              <InlinePinSetup
                onSaved={() => {
                  setPinJustCreated(true);
                  colocaToast.success(t("security_pin_created_toast"));
                }}
                onClose={() => {
                  setPinJustCreated(false);
                  setMethod(null);
                }}
              />
            )}

            {method === "totp" && (
              <div className="animate-fade-down overflow-hidden rounded-md border border-border bg-muted/30 animate-duration-300 motion-reduce:animate-none">
                {pinActive ? (
                  <div className="flex items-start gap-2 border-b border-border bg-primary/5 px-4 py-2.5 text-[12px] text-foreground">
                    <Info size={13} className="mt-0.5 shrink-0 text-primary" aria-hidden="true" />
                    {t("security_totp_will_replace_pin")}
                  </div>
                ) : null}
                {/* The step counter drops to its own line rather than being squeezed into the title: the
                    two together need more than a phone gives, and neither may be clipped. */}
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-border px-4 py-3">
                  <Smartphone size={14} className="shrink-0 text-primary" />
                  <span className="min-w-0 text-[13px] font-semibold text-foreground">
                    {t("security_2fa_totp_setup_title")}
                  </span>
                  {/* Hidden on a phone: the numbered instructions below already carry the sequence, so
                      the counter is the one thing here that can go when the width runs out. */}
                  <span className="ml-auto hidden shrink-0 font-mono text-[10.5px] uppercase leading-tight tracking-[0.04em] text-muted-foreground sm:inline">
                    {t("security_2fa_totp_step", { step: totpStage === "password" ? "1" : "2", total: "2" })}
                  </span>
                </div>

                {totpStage === "password" ? (
                  <TotpPasswordStep onSubmit={startTotpSetup} />
                ) : (
                  <div className="grid animate-fade-up grid-cols-1 gap-5 p-4 animate-duration-300 motion-reduce:animate-none md:grid-cols-[auto_1fr]">
                    {/* QR */}
                    <QrCode value={qrUrl} />

                    <div className="flex min-w-0 flex-col gap-2.5">
                      <div>
                        <div className="text-[13px] font-medium text-foreground">
                          1. {t("enable_twofa_scan_instructions")}
                        </div>
                        <div className="mt-1 text-[12px] text-muted-foreground">
                          {t("enable_twofa_manual_entry")}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 rounded-sm border border-border bg-background px-2.5 py-2">
                        {/* `min-w-0` + `break-all` are what keep the two actions reachable. A base32 secret
                          is 32 characters with nowhere to wrap, so a plain `flex-1` resolves to its
                          min-content width and pushes the buttons out of the panel, where the ancestor's
                          `overflow-hidden` deletes them. The placeholder hid it: its dots have spaces. */}
                        <span
                          className={cn(
                            "min-w-0 flex-1 break-all select-none font-mono text-[12px] tracking-wider transition-all",
                            showSecret ? "select-text blur-0" : "blur-xs",
                          )}
                        >
                          {isLoadingSetup ? "•••••• •••••• ••••••" : secret || "—"}
                        </span>
                        <button
                          type="button"
                          onClick={() => setShowSecret(!showSecret)}
                          aria-label={showSecret ? t("security_hide") : t("security_show")}
                          className="grid h-8 w-8 shrink-0 place-items-center rounded text-muted-foreground hover:bg-foreground/5"
                        >
                          {showSecret ? <EyeOff size={13} /> : <Eye size={13} />}
                        </button>
                        <button
                          type="button"
                          onClick={handleCopySecret}
                          aria-label={t("copy_button")}
                          className="grid h-8 w-8 shrink-0 place-items-center rounded text-muted-foreground hover:bg-foreground/5"
                        >
                          <Copy size={13} />
                        </button>
                      </div>

                      <div className="mt-1">
                        <div className="mb-2 text-[13px] font-medium text-foreground">
                          2. {t("enable_twofa_enter_code")}
                        </div>
                        <ColocaOtpInput
                          value={otp}
                          onValueChange={handleOtpChange}
                          onComplete={(value) => void handleVerify(value)}
                          disabled={!secret || isVerifying}
                          invalid={!!verifyError}
                          aria-label={t("enable_twofa_enter_code")}
                        />
                        {verifyError && (
                          <p
                            role="alert"
                            className="mt-2 flex items-center gap-1.5 text-[12px] font-medium text-destructive"
                          >
                            <AlertTriangle size={12} />
                            {verifyError}
                          </p>
                        )}
                        <div className="mt-2.5 flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:gap-2.5">
                          <ColocaButton
                            variant="primary"
                            onClick={() => void handleVerify()}
                            loading={isVerifying}
                            disabled={otp.length !== 6 || !secret}
                            className="w-full sm:w-auto"
                          >
                            {t("enable_twofa_verify_button")}
                          </ColocaButton>
                          {/* Prose, so not in the mono face the eyebrows and codes use — which also bought
                            back the width that was breaking it over three lines beside the button. */}
                          <span className="text-center text-[11px] text-muted-foreground sm:ml-auto sm:text-right">
                            {t("security_2fa_code_valid_30s")}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Recovery codes — shown only when 2FA is enabled */}
        {enabled && (
          <>
            <div className="my-5 h-px bg-border" />
            <div>
              {/* Three actions and a title do not share a phone's width. Stacked below `sm`, they wrap
                  across the full row instead of into a narrow column pinned against the edge. */}
              <div className="mb-2.5 flex flex-col gap-2.5 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
                <div className="min-w-0">
                  <div className="text-[13px] font-semibold text-foreground">
                    {t("security_recovery_title")}
                  </div>
                  <div className="mt-0.5 text-[12px] text-muted-foreground">{t("security_recovery_sub")}</div>
                </div>
                <div className="flex w-full flex-wrap gap-2 sm:w-auto sm:justify-end">
                  <ColocaButton
                    variant="secondary"
                    size="sm"
                    icon={RotateCw}
                    onClick={handleRegenerateCodes}
                    loading={isRegenerating}
                  >
                    {t("security_recovery_regenerate")}
                  </ColocaButton>
                  {hasCodes ? (
                    <>
                      <ColocaButton
                        variant="secondary"
                        size="sm"
                        icon={hasCopiedCodes ? Check : Copy}
                        onClick={handleCopyCodes}
                      >
                        {hasCopiedCodes ? t("security_recovery_copied") : t("security_recovery_copy_all")}
                      </ColocaButton>
                      <ColocaButton
                        variant="secondary"
                        size="sm"
                        icon={Download}
                        onClick={handleDownloadCodes}
                      >
                        {t("security_recovery_download")}
                      </ColocaButton>
                    </>
                  ) : null}
                </div>
              </div>

              {hasCodes ? (
                <>
                  {/* Above the grid, not under it: the codes cannot be retrieved once this page is left, so
                      the warning has to be read before the thing it is about. */}
                  <ColocaAlert
                    variant="warning"
                    icon={AlertTriangle}
                    heading={t("security_recovery_warning_title")}
                    text={t("security_recovery_warning_body")}
                    className="mb-3"
                  />
                  <div className="rounded-md border border-dashed border-border bg-muted/30 p-3.5">
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                      {codes.map((code, i) => (
                        <div key={code} className="flex items-center gap-1.5">
                          <span className="w-5 shrink-0 font-mono text-[10.5px] text-muted-foreground/70">
                            {String(i + 1).padStart(2, "0")}
                          </span>
                          {/* The letter spacing is dropped on the narrowest screens rather than the code:
                              a recovery code is retyped character by character, so truncating one is
                              worse than letting it wrap. */}
                          <span className="font-mono text-[12.5px] font-semibold tracking-normal text-foreground sm:tracking-wider">
                            {code}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <div className="rounded-md border border-dashed border-border bg-muted/30 p-3.5 text-center text-[12px] text-muted-foreground">
                  <Info size={14} className="mx-auto mb-1.5" />
                  {t("security_recovery_empty_hint")}
                </div>
              )}
            </div>
          </>
        )}

        {emailBackup ? (
          <>
            <div className="my-5 h-px bg-border" />
            <EmailBackupCard />
          </>
        ) : null}
      </SettingsSection>

      {disableOpen && (
        <Disable2FAModal
          isOpen={disableOpen}
          onClose={() => setDisableOpen(false)}
          onSuccess={handleDisableSuccess}
        />
      )}

      <SecurityPinDialog
        open={pinDialog !== null}
        onOpenChange={(open) => !open && setPinDialog(null)}
        mode={pinDialog ?? "change"}
        onDone={() => colocaToast.success(t("security_pin_changed_toast"))}
      />
      <RemoveSecurityPinDialog open={removePinOpen} onOpenChange={setRemovePinOpen} />
    </>
  );
}

/**
 * The first step of authenticator enrolment: the account password, which the server checks before minting a
 * secret. Wrong attempts count toward the login lock, and the field shakes so the refusal is noticed.
 */
function TotpPasswordStep({ onSubmit }: { onSubmit: (password: string) => Promise<string | null> }) {
  const { t } = useTranslations("settings");
  const { t: tg } = useTranslations("global");
  const schema = useMemo(
    () => z.object({ password: z.string().min(1, t("security_totp_password_required")) }),
    [t],
  );
  const form = useZodForm(schema, { password: "" });
  const [shakeKey, setShakeKey] = useState(0);

  const submit = async ({ password }: { password: string }) => {
    const failure = await onSubmit(password);
    if (!failure) {
      form.reset({ password: "" });
      return;
    }
    form.setError("password", { message: failure });
    setShakeKey((key) => key + 1);
    // The shake remounts the field, so focus goes back to it once the new one is on screen.
    window.requestAnimationFrame(() => form.setFocus("password"));
  };

  return (
    <div className="animate-fade-up p-4 animate-duration-300 motion-reduce:animate-none">
      <p className="mb-3 text-[12.5px] text-muted-foreground">{t("security_totp_password_intro")}</p>
      <ColocaForm form={form} onSubmit={submit} className="max-w-sm space-y-3">
        <div key={shakeKey} className={cn(shakeKey > 0 && "animate-shake motion-reduce:animate-none")}>
          <ColocaPasswordField
            name="password"
            label={tg("security_pin_password_label")}
            autoComplete="current-password"
            showLabel={tg("security_pin_password_show")}
            hideLabel={tg("security_pin_password_hide")}
          />
        </div>
        <div className="flex justify-end">
          <ColocaButton
            type="submit"
            variant="primary"
            iconTrailing={ArrowRight}
            loading={form.formState.isSubmitting}
            className="w-full sm:w-auto"
          >
            {t("security_totp_password_submit")}
          </ColocaButton>
        </div>
      </ColocaForm>
    </div>
  );
}

/**
 * Creating the PIN right in the section, under its method card: stepper, the step, and the actions under it.
 * Mounted per opening, so choosing the PIN again after removing one starts from the first step.
 */
function InlinePinSetup({ onSaved, onClose }: { onSaved: () => void; onClose: () => void }) {
  const { t } = useTranslations("settings");
  const { t: tg } = useTranslations("global");
  const pinSetup = useSecurityPinSetup({ onSaved });

  return (
    <div className="mb-1 animate-fade-down overflow-hidden rounded-md border border-border bg-muted/30 animate-duration-300 motion-reduce:animate-none">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-border px-4 py-3">
        <KeyRound size={14} className="shrink-0 text-primary" />
        <span className="min-w-0 text-[13px] font-semibold text-foreground">
          {t("security_pin_setup_title")}
        </span>
      </div>
      <div className="flex flex-col gap-4 p-4">
        {pinSetup.step !== "done" ? <SecurityPinStepper step={pinSetup.step} /> : null}
        <div className="mx-auto w-full max-w-sm">
          <SecurityPinSetupBody controller={pinSetup} />
        </div>
        <SecurityPinSetupActions
          controller={pinSetup}
          submitLabel={tg("security_pin_submit_create")}
          done={{ label: tg("security_pin_done_close"), onClick: onClose }}
        />
      </div>
    </div>
  );
}

interface PinStatusBannerProps {
  /** The lock the PIN is under, as a sentence; null when it works. */
  lockText: string | null;
  onChange: () => void;
  onRemove: () => void;
}

/** "Protected with a PIN", what it is for, and its two controls; a lock turns it into the way out of the lock. */
function PinStatusBanner({ lockText, onChange, onRemove }: PinStatusBannerProps) {
  const { t } = useTranslations("settings");
  return (
    <div className="mb-[18px] flex animate-fade-up flex-col gap-3 rounded-md border border-primary/30 bg-primary/5 p-3.5 animate-duration-300 motion-reduce:animate-none">
      <div className="flex flex-wrap items-center gap-3.5">
        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-primary/30 bg-background text-primary">
          <KeyRound size={16} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-semibold text-foreground">{t("security_pin_active_title")}</div>
          <div className="mt-0.5 text-[12px] text-muted-foreground">{t("security_pin_active_sub")}</div>
        </div>
        <div className="flex w-full flex-col-reverse gap-2 sm:w-auto sm:flex-row">
          <ColocaButton
            variant="ghost"
            size="sm"
            icon={XCircle}
            onClick={onRemove}
            className="text-destructive"
          >
            {t("security_pin_remove")}
          </ColocaButton>
          <ColocaButton variant="primary" size="sm" icon={KeyRound} onClick={onChange}>
            {lockText ? t("security_pin_locked_action") : t("security_pin_change")}
          </ColocaButton>
        </div>
      </div>
      {lockText ? (
        <ColocaAlert
          variant="warning"
          icon={Lock}
          heading={t("security_pin_locked_title")}
          text={lockText}
          className="animate-fade-down animate-duration-300 motion-reduce:animate-none"
        />
      ) : null}
    </div>
  );
}

interface MethodCardProps {
  id: string;
  icon: typeof Mail;
  /** An animated miniature of how the method works, drawn above the name. */
  illustration?: ReactNode;
  name: string;
  sub: string;
  tag?: string;
  tagVariant?: "success" | "primary" | "secondary";
  /** One line on how the email backup comes with this method. */
  note?: string;
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  className?: string;
}

function MethodCard({
  icon: Icon,
  illustration,
  name,
  sub,
  tag,
  tagVariant = "secondary",
  note,
  active,
  disabled,
  onClick,
  className,
}: MethodCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      className={cn(
        "relative flex flex-col gap-3 rounded-md border p-3.5 text-left transition-colors",
        className,
        active
          ? "border-primary bg-primary/5 shadow-[0_0_0_3px_color-mix(in_oklch,var(--color-primary)_12%,transparent)]"
          : "border-border bg-background",
        disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:border-foreground/20",
      )}
    >
      {illustration ? <MethodIllustrationStage>{illustration}</MethodIllustrationStage> : null}
      <div className="flex items-start gap-3">
        <div
          className={cn(
            "grid h-8 w-8 shrink-0 place-items-center rounded-lg border",
            active
              ? "border-primary/30 bg-primary/10 text-primary"
              : "border-border bg-muted text-muted-foreground",
          )}
        >
          <Icon size={16} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[13px] font-semibold text-foreground">{name}</span>
            {tag && (
              <ColocaBadge
                variant={
                  tagVariant === "primary" ? "primary" : tagVariant === "success" ? "success" : "secondary"
                }
              >
                {tag}
              </ColocaBadge>
            )}
          </div>
          <div className="mt-0.5 text-[12px] text-muted-foreground">{sub}</div>
          {note ? (
            <div className="mt-1.5 flex items-center gap-1.5 text-[11.5px] font-medium text-foreground/80">
              <Mail size={12} className="shrink-0 text-primary" aria-hidden="true" />
              {note}
            </div>
          ) : null}
        </div>
      </div>
      {active && (
        <span
          className="absolute right-3 top-3 grid h-4 w-4 place-items-center rounded-full bg-primary text-primary-foreground"
          aria-hidden
        >
          <Check size={10} />
        </span>
      )}
    </button>
  );
}
